import cx from "classnames";
import Script from "next/script";
import { useEffect, useId, useRef, useState } from "react";

import { AddressData } from "@/api/address/types";
import { formatAddressObject } from "@/utils/format";

import { LoadingSpinner } from "../elements/LoadingSpinner";
import styles from "./styles.module.css";

export type AddressPickerProps = {
  onUpdate: (Record: AddressData) => void;
  onChange?: () => void;
  className?: string;
  prefillAddress?: {
    street_address: string;
    postal_code: string;
    region: string;
  };
};

// Make DI a valid prop on window
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DI: any;
  }
}

const config = {
  apiKey: process.env.NEXT_PUBLIC_DI_ADDRESS_HELPER_WIDGET,
};

export const AddressPicker = ({
  onUpdate,
  onChange,
  className,
  prefillAddress,
}: AddressPickerProps) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(true);
  const id = `addressHelperWidget-${useId().replace(/:/g, "")}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const diahRef = useRef<any>(null);

  useEffect(() => {
    function handleChangeEvent() {
      if (onChange) onChange();
      const DIaddress = diahRef.current?.getSelectedAddress();
      const complete = diahRef.current?.isComplete();

      if (complete) {
        const formattedAddress = formatAddressObject(DIaddress);
        onUpdate(formattedAddress);
      }
    }

    if (isLoaded || window.DI) {
      setShowSpinner(false);
      if (!diahRef.current) {
        diahRef.current = new DI.AddressHelper(id, config);
        if (prefillAddress) {
          diahRef.current.parseStreetAddressAndZipCity(
            prefillAddress.street_address,
            prefillAddress.postal_code,
            prefillAddress.region
          );
        }
      }
      diahRef.current.selectionChangeEvent.subscribe(handleChangeEvent);
    }

    return () =>
      diahRef.current?.selectChangeEvent?.unsubscribe(handleChangeEvent);
  }, [isLoaded, onChange]);

  return (
    <>
      <div id={id} className={cx(styles.container, className)} />

      {showSpinner && <LoadingSpinner />}

      <Script
        src="https://www.di.no/addresshelper/diah.js"
        onLoad={() => {
          setIsLoaded(true);
        }}
      />
    </>
  );
};
